import { useEffect, useState } from "react";
import "./TopNav.css";

/**
 * Fixed crimson nav bar. Tabs smooth-scroll to their section; the tab whose
 * section is currently in view gets the gold underline.
 *
 * Active-section detection uses scroll position (not IntersectionObserver) so
 * that short sections — like a small Contact — can never be skipped: the active
 * tab is simply the last section whose top has crossed a line near the top of
 * the viewport, with a special case for the bottom of the page.
 */
export default function TopNav({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const ids = sections.map((s) => s.id);

    const update = () => {
      // Detection line ~30% down from the top of the viewport.
      const line = window.innerHeight * 0.3;

      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - line <= 0) current = id;
      }

      // At the very bottom of the page, force the last section active — a short
      // final section may be impossible to scroll up to the detection line.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) current = ids[ids.length - 1];

      setActiveId(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [sections]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className="topnav" aria-label="Primary">
      <ul className="topnav__tabs">
        {sections.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              className={"topnav__tab" + (activeId === s.id ? " is-active" : "")}
              aria-current={activeId === s.id ? "true" : undefined}
              onClick={() => go(s.id)}
            >
              {s.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
